import { DEFAULT_WIDGET_PAGE_ID, getDB, normalizeWidgetPageId } from '../core';
import type { WidgetItem } from '@/types';

export async function addWidget(widget: WidgetItem): Promise<void> {
  const db = await getDB();
  await db.put('widgets', normalizeWidgetPageId(widget));
}

export async function getAllWidgets(): Promise<WidgetItem[]> {
  const db = await getDB();
  const widgets = await db.getAllFromIndex('widgets', 'by-order');
  return widgets.map(normalizeWidgetPageId);
}

export async function getWidgetsByPage(pageId = DEFAULT_WIDGET_PAGE_ID): Promise<WidgetItem[]> {
  const db = await getDB();
  const range = IDBKeyRange.bound(
    [pageId, Number.MIN_SAFE_INTEGER],
    [pageId, Number.MAX_SAFE_INTEGER],
  );
  const widgets = await db.getAllFromIndex('widgets', 'by-page-order', range);
  return widgets.map(normalizeWidgetPageId);
}

async function resolveWidgetPageId(pageId = DEFAULT_WIDGET_PAGE_ID): Promise<string> {
  const db = await getDB();
  const requestedPageId = pageId || DEFAULT_WIDGET_PAGE_ID;
  const requestedPage = await db.get('widgetPages', requestedPageId);
  if (requestedPage) return requestedPageId;

  const defaultPage = await db.get('widgetPages', DEFAULT_WIDGET_PAGE_ID);
  if (defaultPage) return DEFAULT_WIDGET_PAGE_ID;

  const [firstPage] = await db.getAllFromIndex('widgetPages', 'by-order');
  return firstPage?.id ?? DEFAULT_WIDGET_PAGE_ID;
}

export async function createWidgetForPage(
  dataId: string,
  pageId = DEFAULT_WIDGET_PAGE_ID,
  order?: number,
): Promise<WidgetItem> {
  const targetPageId = await resolveWidgetPageId(pageId);
  const existing = order === undefined ? await getWidgetsByPage(targetPageId) : [];
  const minOrder = existing.length > 0 ? Math.min(...existing.map((widget) => widget.order)) : 0;
  const widget: WidgetItem = {
    id: crypto.randomUUID(),
    pageId: targetPageId,
    dataId,
    x: 0,
    y: 0,
    width: 1,
    height: 1,
    order: order ?? minOrder - 1,
  };

  await addWidget(widget);
  return widget;
}

export async function deleteWidget(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('widgets', id);
}

export async function updateWidget(widget: WidgetItem): Promise<void> {
  const db = await getDB();
  await db.put('widgets', normalizeWidgetPageId(widget));
}
